/**
 * Custom hook for visitor offline functionality
 * Manages offline state, sync status, and pending actions
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import visitorLocalStorage from '../../../utils/visitorLocalStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const useVisitorOffline = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [pendingActions, setPendingActions] = useState([]);
  const [syncStatus, setSyncStatus] = useState('idle'); // idle, syncing, success, error
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [offlineStats, setOfflineStats] = useState({
    visitors: 0,
    visitor_logs: 0,
    visitor_photos: 0,
    visitor_documents: 0,
    pending_actions: 0
  });

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      console.log('Back online - starting sync...');
      syncPendingActions();
    };
    
    const handleOffline = () => {
      setIsOffline(true);
      console.log('Gone offline - switching to offline mode');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load pending actions and stats
  useEffect(() => {
    loadPendingActions();
    loadOfflineStats();
  }, []);

  // Load pending actions from localStorage
  const loadPendingActions = useCallback(async () => {
    try {
      const actions = await visitorLocalStorage.getPendingActions();
      setPendingActions(actions);
    } catch (error) {
      console.error('Error loading pending actions:', error);
    }
  }, []);

  // Load offline statistics
  const loadOfflineStats = useCallback(async () => {
    try {
      const stats = await visitorLocalStorage.getStats();
      setOfflineStats(stats);
    } catch (error) {
      console.error('Error loading offline stats:', error);
    }
  }, []);

  // Add pending action
  const addPendingAction = useCallback(async (actionType, data, visitorId = null) => {
    try {
      const action = {
        id: `action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        action_type: actionType,
        data: data,
        visitor_id: visitorId,
        created_at: new Date().toISOString(),
        retry_count: 0,
        max_retries: 3
      };

      await visitorLocalStorage.savePendingAction(action);
      await loadPendingActions();
      await loadOfflineStats();
      
      return action;
    } catch (error) {
      console.error('Error adding pending action:', error);
      throw error;
    }
  }, [loadPendingActions, loadOfflineStats]);

  // Remove pending action
  const removePendingAction = useCallback(async (actionId) => {
    try {
      await visitorLocalStorage.deletePendingAction(actionId);
      await loadPendingActions();
      await loadOfflineStats();
    } catch (error) {
      console.error('Error removing pending action:', error);
    }
  }, [loadPendingActions, loadOfflineStats]);

  // Sync pending actions with server
  const syncPendingActions = useCallback(async () => {
    if (isOffline || pendingActions.length === 0) {
      return;
    }

    setSyncStatus('syncing');
    
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const action of pendingActions) {
        try {
          let endpoint = '';
          let method = 'POST';
          let body = null;

          // Map action types to API endpoints
          switch (action.action_type) {
            case 'REGISTER_VISITOR':
              endpoint = '/api/visitors/register';
              body = JSON.stringify(action.data);
              break;
            case 'CHECKIN_VISITOR':
              endpoint = `/api/visitors/${action.visitor_id}/checkin`;
              break;
            case 'CHECKOUT_VISITOR':
              endpoint = `/api/visitors/${action.visitor_id}/checkout`;
              break;
            case 'APPROVE_VISITOR':
              endpoint = `/api/visitors/${action.visitor_id}/approve`;
              break;
            case 'REJECT_VISITOR':
              endpoint = `/api/visitors/${action.visitor_id}/reject`;
              break;
            case 'UPDATE_VISITOR':
              endpoint = `/api/visitors/${action.visitor_id}`;
              method = 'PUT';
              body = JSON.stringify(action.data);
              break;
            case 'DELETE_VISITOR':
              endpoint = `/api/visitors/${action.visitor_id}`;
              method = 'DELETE';
              break;
            default:
              console.warn(`Unknown action type: ${action.action_type}`);
              continue;
          }

          // Make API call
          const response = await axios({
            url: endpoint,
            method,
            baseURL: BACKEND_URL,
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json',
            },
            ...(body && { data: JSON.parse(body) }),
          });

          // Success - remove from pending actions
          await removePendingAction(action.id);
          successCount++;
          
          console.log(`Successfully synced action: ${action.action_type}`);
        } catch (error) {
          console.error(`Failed to sync action ${action.id}:`, error);
          
          // Increment retry count
          const updatedAction = {
            ...action,
            retry_count: (action.retry_count || 0) + 1,
            last_error: error.message,
            last_retry_at: new Date().toISOString()
          };

          // Remove if max retries reached
          if (updatedAction.retry_count >= (action.max_retries || 3)) {
            console.warn(`Max retries reached for action ${action.id}, removing`);
            await removePendingAction(action.id);
          } else {
            await visitorLocalStorage.savePendingAction(updatedAction);
          }
          
          errorCount++;
        }
      }

      // Update sync status
      if (errorCount === 0) {
        setSyncStatus('success');
      } else {
        setSyncStatus('error');
      }
      
      setLastSyncTime(new Date().toISOString());
      
      // Reload pending actions and stats
      await loadPendingActions();
      await loadOfflineStats();
      
      console.log(`Sync completed: ${successCount} success, ${errorCount} errors`);
      
    } catch (error) {
      console.error('Error during sync:', error);
      setSyncStatus('error');
    }
  }, [isOffline, pendingActions, removePendingAction, loadPendingActions, loadOfflineStats]);

  // Manual sync trigger
  const triggerSync = useCallback(async () => {
    if (!isOffline) {
      await syncPendingActions();
    }
  }, [isOffline, syncPendingActions]);

  // Clear all offline data
  const clearOfflineData = useCallback(async () => {
    try {
      await visitorLocalStorage.clearAllData();
      setPendingActions([]);
      setOfflineStats({
        visitors: 0,
        visitor_logs: 0,
        visitor_photos: 0,
        visitor_documents: 0,
        pending_actions: 0
      });
      console.log('All offline data cleared');
    } catch (error) {
      console.error('Error clearing offline data:', error);
      throw error;
    }
  }, []);

  // Check if visitor exists offline
  const hasOfflineVisitor = useCallback(async (visitorId) => {
    try {
      const visitor = await visitorLocalStorage.getVisitor(visitorId);
      return !!visitor;
    } catch (error) {
      console.error('Error checking offline visitor:', error);
      return false;
    }
  }, []);

  // Get offline visitor data
  const getOfflineVisitor = useCallback(async (visitorId) => {
    try {
      return await visitorLocalStorage.getVisitor(visitorId);
    } catch (error) {
      console.error('Error getting offline visitor:', error);
      return null;
    }
  }, []);

  // Save visitor offline
  const saveVisitorOffline = useCallback(async (visitor) => {
    try {
      const offlineVisitor = {
        ...visitor,
        is_offline: true,
        last_synced: new Date().toISOString()
      };
      
      await visitorLocalStorage.saveVisitor(offlineVisitor);
      await loadOfflineStats();
      
      return offlineVisitor;
    } catch (error) {
      console.error('Error saving visitor offline:', error);
      throw error;
    }
  }, [loadOfflineStats]);

  // Get connection status info
  const getConnectionInfo = useCallback(() => {
    return {
      isOnline: !isOffline,
      isOffline,
      pendingActionsCount: pendingActions.length,
      syncStatus,
      lastSyncTime,
      canSync: !isOffline && pendingActions.length > 0
    };
  }, [isOffline, pendingActions.length, syncStatus, lastSyncTime]);

  return {
    isOffline,
    isOnline: !isOffline,
    pendingActions,
    syncStatus,
    lastSyncTime,
    offlineStats,
    addPendingAction,
    removePendingAction,
    syncPendingActions,
    triggerSync,
    clearOfflineData,
    hasOfflineVisitor,
    getOfflineVisitor,
    saveVisitorOffline,
    getConnectionInfo,
    loadPendingActions,
    loadOfflineStats
  };
};

export default useVisitorOffline;